import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon, Edit as EditIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import { RootState } from '../../../store';
import { tradersService, usersService } from '../../../services';
import { TraderGroup, TraderGroupMember, TraderGroupReferralReportResponse, User } from '../../../types';
import { RoleEnum } from '../../../types/auth/roles';
import { useNotification } from '../../../contexts/NotificationContext';
import AnimatedPage from '../../../components/AnimatedPage';
import TelegramChatsSection from '@/components/TelegramChatsSection';
import type { TelegramChat } from '@/types/common/telegram';

interface TraderGroupsRef {
  handleOpenDialog: () => void;
}

type GroupFormData = {
  name: string;
  owner_id: string;
};

const initialFormData: GroupFormData = {
  name: '',
  owner_id: '',
};

const initialReportFilters = {
  mode: 'groups' as 'groups' | 'agents',
  group_id: '',
  owner_id: '',
  start_date: '',
  end_date: '',
};

const TraderGroups = forwardRef<TraderGroupsRef>((props, ref) => {
  const [groups, setGroups] = useState<TraderGroup[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TraderGroup | null>(null);
  const [formData, setFormData] = useState<GroupFormData>(initialFormData);
  const [report, setReport] = useState<TraderGroupReferralReportResponse | null>(null);
  const [reportFilters, setReportFilters] = useState(initialReportFilters);
  const [appliedReportFilters, setAppliedReportFilters] = useState(initialReportFilters);
  const [isExporting, setIsExporting] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [membersGroupName, setMembersGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState<TraderGroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [telegramChats, setTelegramChats] = useState<TelegramChat[]>([]);
  const [telegramChatsLoading, setTelegramChatsLoading] = useState(false);
  const [telegramChatsError, setTelegramChatsError] = useState<string | null>(null);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { showNotification } = useNotification();

  const canCreate = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_CREATE), [currentUser]);
  const canUpdate = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_UPDATE), [currentUser]);
  const canDelete = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_DELETE), [currentUser]);

  const ownerUsers = useMemo(
    () => users.filter((user) => user.role === RoleEnum.TRADERS_OWNER || user.role === RoleEnum.ADMIN || user.is_superuser),
    [users],
  );

  const groupById = useMemo(
    () => groups.reduce<Record<string, TraderGroup>>((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {}),
    [groups],
  );

  const formatMoney = (value: string | number | undefined | null) => {
    const numeric = Number(value || 0);
    return Number.isFinite(numeric) ? numeric.toFixed(2) : '0.00';
  };

  const buildReportParams = (filters = reportFilters) => ({
    mode: filters.mode,
    group_id: filters.group_id || undefined,
    owner_id: filters.owner_id || undefined,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [groupsData, usersData, reportData] = await Promise.all([
        tradersService.getTraderGroups(),
        usersService.getUsers(),
        tradersService.getTraderGroupReferralReport(buildReportParams()),
      ]);
      setGroups(groupsData);
      setUsers(usersData);
      setReport(reportData);
      setError(null);
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Ошибка загрузки групп трейдеров';
      setError(message);
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setTelegramChats([]);
    setTelegramChatsError(null);
    if (!dialogOpen || !selectedGroup) {
      setTelegramChatsLoading(false);
      return;
    }

    let active = true;
    setTelegramChatsLoading(true);
    tradersService.getTraderGroupTelegramChats(selectedGroup.id)
      .then((data) => {
        if (active) setTelegramChats(data);
      })
      .catch(() => {
        if (active) setTelegramChatsError('Не удалось загрузить чат с тимлидом');
      })
      .finally(() => {
        if (active) setTelegramChatsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [dialogOpen, selectedGroup]);

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => {
      if (!canCreate) {
        showNotification('У вас нет прав для создания группы', 'error');
        return;
      }
      setSelectedGroup(null);
      setFormData(initialFormData);
      setDialogOpen(true);
    },
  }));

  const handleReportFilterChange = (field: keyof typeof reportFilters) => (event: any) => {
    setReportFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
      ...(field === 'mode' && event.target.value === 'agents' ? { group_id: '' } : {}),
    }));
  };

  const handleReportSearch = async () => {
    try {
      const reportData = await tradersService.getTraderGroupReferralReport(buildReportParams());
      setReport(reportData);
      setAppliedReportFilters(reportFilters);
    } catch (err: any) {
      showNotification(err.response?.data?.detail || err.message || 'Ошибка загрузки отчёта по рефке', 'error');
    }
  };

  const handleReportReset = async () => {
    setReportFilters(initialReportFilters);
    try {
      const reportData = await tradersService.getTraderGroupReferralReport({});
      setReport(reportData);
      setAppliedReportFilters(initialReportFilters);
    } catch (err: any) {
      showNotification(err.response?.data?.detail || err.message || 'Ошибка загрузки отчёта по рефке', 'error');
    }
  };

  const handleOpenMembers = async (groupId: string, groupName: string) => {
    setMembersDialogOpen(true);
    setMembersGroupName(groupName);
    setGroupMembers([]);
    setMembersError(null);
    setMembersLoading(true);
    try {
      const appliedParams = buildReportParams(appliedReportFilters);
      const members = await tradersService.getTraderGroupMembers(groupId, {
        start_date: appliedParams.start_date,
        end_date: appliedParams.end_date,
      });
      setGroupMembers(members);
    } catch (err: any) {
      setMembersError(err.response?.data?.detail || err.message || 'Не удалось загрузить трейдеров группы');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleReportExport = async (format: 'csv' | 'xlsx') => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const response = await tradersService.exportTraderGroupReferralReport({
        format,
        ...buildReportParams(appliedReportFilters),
      });
      const contentType =
        response.headers['content-type'] ||
        (format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv;charset=utf-8');
      const blob = new Blob([response.data], { type: contentType });
      const disposition = response.headers['content-disposition'];
      const filenameMatch = disposition?.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)/i);
      const decodedFilename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, ''))
        : `group_referrals.${format}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = decodedFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification('Файл успешно выгружен', 'success');
    } catch (err: any) {
      showNotification(err.response?.data?.detail || err.message || 'Ошибка выгрузки отчёта по рефке', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleEdit = (group: TraderGroup) => {
    if (!canUpdate) {
      showNotification('У вас нет прав для редактирования группы', 'error');
      return;
    }
    setSelectedGroup(group);
    setFormData({ name: group.name, owner_id: group.owner_id });
    setDialogOpen(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      if (selectedGroup) {
        await tradersService.updateTraderGroup(selectedGroup.id, formData);
        showNotification('Группа обновлена', 'success');
      } else {
        await tradersService.createTraderGroup(formData);
        showNotification('Группа создана', 'success');
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err: any) {
      showNotification(err.response?.data?.detail || err.message || 'Ошибка сохранения группы', 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedGroup) return;
    try {
      await tradersService.deleteTraderGroup(selectedGroup.id);
      showNotification('Группа удалена', 'success');
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      await fetchData();
    } catch (err: any) {
      showNotification(err.response?.data?.detail || err.message || 'Ошибка удаления группы', 'error');
    }
  };

  const openDelete = (group: TraderGroup) => {
    if (!canDelete) {
      showNotification('У вас нет прав для удаления группы', 'error');
      return;
    }
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  return (
    <AnimatedPage>
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Рефка по группам</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Источник</InputLabel>
                <Select
                  label="Источник"
                  value={reportFilters.mode}
                  onChange={handleReportFilterChange('mode')}
                >
                  <MenuItem value="groups">По группам</MenuItem>
                  <MenuItem value="agents">По агентам</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Дата от"
                type="date"
                value={reportFilters.start_date}
                onChange={handleReportFilterChange('start_date')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Дата до"
                type="date"
                value={reportFilters.end_date}
                onChange={handleReportFilterChange('end_date')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>ТЛ</InputLabel>
                <Select
                  label="ТЛ"
                  value={reportFilters.owner_id}
                  onChange={handleReportFilterChange('owner_id')}
                >
                  <MenuItem value="">Все</MenuItem>
                  {ownerUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username} ({user.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Группа</InputLabel>
                <Select
                  label="Группа"
                  value={reportFilters.group_id}
                  onChange={handleReportFilterChange('group_id')}
                  disabled={reportFilters.mode === 'agents'}
                >
                  <MenuItem value="">Все</MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Общая рефка: {formatMoney(report?.total_referral_income)} · Оборот SUCCESS: {formatMoney(report?.total_turnover)} · Доход трейдеров: {formatMoney(report?.total_trader_income)} · Время: MSK, по обновлению
                </Typography>
              </Box>
              <Button variant="outlined" onClick={handleReportReset}>
                Сбросить
              </Button>
              <Button variant="contained" onClick={handleReportSearch}>
                Применить
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={isExporting}
                onClick={() => handleReportExport('xlsx')}
              >
                XLSX
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={isExporting}
                onClick={() => handleReportExport('csv')}
              >
                CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Группа</TableCell>
                  <TableCell>Владелец</TableCell>
                  <TableCell align="right">Трейдеров</TableCell>
                  <TableCell align="right">Успешных</TableCell>
                  <TableCell align="right">Оборот</TableCell>
                  <TableCell align="right">Доход трейдеров</TableCell>
                  <TableCell align="right">Рефка</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report?.items || []).map((row) => {
                  const isAgentMode = reportFilters.mode === 'agents';
                  const reportRow = row as TraderGroupReferralReportResponse['items'][number];
                  const group = reportRow.group_id ? groupById[reportRow.group_id] : undefined;
                  const rowKey = isAgentMode
                    ? `agent-${reportRow?.owner_id || reportRow?.owner_name || reportRow?.referral_income}`
                    : `group-${reportRow.group_id || reportRow.group_name}`;
                  return (
                    <TableRow key={rowKey}>
                      <TableCell>{reportRow?.group_name || (isAgentMode ? 'Ставки' : '-')}</TableCell>
                      <TableCell>{reportRow?.owner_name || reportRow?.owner_id || '-'}</TableCell>
                      <TableCell align="right">
                        {!isAgentMode && reportRow.group_id ? (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleOpenMembers(reportRow.group_id!, reportRow.group_name || '-')}
                            sx={{ minWidth: 0, fontWeight: 700 }}
                          >
                            {reportRow.traders_count || 0}
                          </Button>
                        ) : (reportRow?.traders_count || 0)}
                      </TableCell>
                      <TableCell align="right">{reportRow?.successful_transactions || 0}</TableCell>
                      <TableCell align="right">{formatMoney(reportRow?.turnover)}</TableCell>
                      <TableCell align="right">{formatMoney(reportRow?.trader_income)}</TableCell>
                      <TableCell align="right">{formatMoney(reportRow?.referral_income)}</TableCell>
                      <TableCell>
                        {!isAgentMode && group && (
                        <Stack direction="row" spacing={1}>
                          <Tooltip title={canUpdate ? 'Редактировать' : 'Недостаточно прав'}>
                            <span>
                              <IconButton size="small" onClick={() => handleEdit(group)} disabled={!canUpdate}>
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDelete ? 'Удалить' : 'Недостаточно прав'}>
                            <span>
                              <IconButton size="small" color="error" onClick={() => openDelete(group)} disabled={!canDelete}>
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!!report?.items.length && (
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: 2, borderColor: 'divider' } }}>
                    <TableCell>ИТОГО</TableCell>
                    <TableCell>{reportFilters.mode === 'agents' ? 'Все агенты' : 'Все группы'}</TableCell>
                    <TableCell align="right">{report.total_traders_count || 0}</TableCell>
                    <TableCell align="right">{report.total_successful_transactions || 0}</TableCell>
                    <TableCell align="right">{formatMoney(report.total_turnover)}</TableCell>
                    <TableCell align="right">{formatMoney(report.total_trader_income)}</TableCell>
                    <TableCell align="right">{formatMoney(report.total_referral_income)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
                {!report?.items.length && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary">
                        {reportFilters.mode === 'agents' ? 'Данные по агентам не найдены' : 'Группы трейдеров не найдены'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={membersDialogOpen} onClose={() => setMembersDialogOpen(false)} maxWidth="xl" fullWidth>
          <DialogTitle>Трейдеры группы «{membersGroupName}»</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Период: {appliedReportFilters.start_date || 'без ограничения'} — {appliedReportFilters.end_date || 'без ограничения'} · MSK, по времени обновления
            </Typography>
            {membersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : membersError ? (
              <Alert severity="error">{membersError}</Alert>
            ) : groupMembers.length ? (
              <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Трейдер</TableCell>
                      <TableCell>Состояние</TableCell>
                      <TableCell align="right">Обработано</TableCell>
                      <TableCell align="right">Успешных</TableCell>
                      <TableCell align="right">Неуспешных</TableCell>
                      <TableCell align="right">Конверсия, %</TableCell>
                      <TableCell align="right">Оборот IN</TableCell>
                      <TableCell align="right">Оборот OUT</TableCell>
                      <TableCell align="right">Доход трейдера</TableCell>
                      <TableCell align="right">Рефка</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>{member.name || member.id}</TableCell>
                        <TableCell>
                          {member.is_deled ? 'В архиве' : member.is_active ? 'Активен' : 'Неактивен'}
                        </TableCell>
                        <TableCell align="right">{member.processed_transactions}</TableCell>
                        <TableCell align="right">{member.successful_transactions}</TableCell>
                        <TableCell align="right">{member.unsuccessful_transactions}</TableCell>
                        <TableCell align="right">
                          {member.success_rate_percent == null ? '—' : formatMoney(member.success_rate_percent)}
                        </TableCell>
                        <TableCell align="right">{formatMoney(member.turnover_in)}</TableCell>
                        <TableCell align="right">{formatMoney(member.turnover_out)}</TableCell>
                        <TableCell align="right">{formatMoney(member.trader_income)}</TableCell>
                        <TableCell align="right">{formatMoney(member.referral_income)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">В группе нет трейдеров</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMembersDialogOpen(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedGroup ? 'Редактировать группу' : 'Создать группу'}</DialogTitle>
          <Box component="form" onSubmit={handleSubmit}>
            <DialogContent>
              <Stack spacing={2}>
                <TextField
                  label="Название"
                  value={formData.name}
                  onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                  required
                  fullWidth
                />
                <FormControl fullWidth required>
                  <InputLabel>Владелец</InputLabel>
                  <Select
                    label="Владелец"
                    value={formData.owner_id}
                    onChange={(event: SelectChangeEvent) => setFormData((prev) => ({ ...prev, owner_id: event.target.value }))}
                  >
                    {ownerUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.username} ({user.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedGroup && (
                  <TelegramChatsSection
                    chats={telegramChats}
                    loading={telegramChatsLoading}
                    error={telegramChatsError}
                    emptyText="Чат с тимлидом для этой группы не добавлен"
                    ownerType="TRADER_GROUP"
                    ownerLabel={selectedGroup.name}
                    onCreate={currentUser && (currentUser.is_superuser || currentUser.role === RoleEnum.ADMIN || currentUser.role === RoleEnum.CLIENT_MANAGER)
                      ? (payload) => tradersService.createTraderGroupTelegramChat(selectedGroup.id, payload)
                      : undefined}
                    onEdit={currentUser && (currentUser.is_superuser || currentUser.role === RoleEnum.ADMIN || currentUser.role === RoleEnum.CLIENT_MANAGER)
                      ? (chatId, payload) => tradersService.updateTraderGroupTelegramChat(selectedGroup.id, chatId, payload)
                      : undefined}
                    onChanged={(updated) => setTelegramChats((items) => (
                      items.some((item) => item.id === updated.id)
                        ? items.map((item) => item.id === updated.id ? updated : item)
                        : [...items, updated]
                    ))}
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
              <Button type="submit" variant="contained">
                Сохранить
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Удалить группу?</DialogTitle>
          <DialogContent>
            <Typography>
              Группа будет удалена, а привязанные трейдеры останутся без группы.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
            <Button color="error" variant="contained" onClick={handleDelete}>
              Удалить
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AnimatedPage>
  );
});

export default TraderGroups;
